import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { PageHead, Skel, Empty, Stat } from '../components/ui'
import { number, pct, thaiDateTime } from '../lib/format'

/**
 * Insider-threat / UEBA (part 109). The server builds a per-user baseline from audit events —
 * action mix, hour-of-day histogram, daily volume — and scores each user against their peer
 * group. This page never calculates a score: it displays the stored score and the stored feature
 * breakdown, so what an analyst sees is exactly what was recorded when the score was produced.
 */

type Band = 'low' | 'elevated' | 'high' | 'critical'

interface ScoreView {
  id: string
  user_id: string
  display_name: string
  peer_group: string
  risk: number
  band: Band
  band_label: string
  explanation: string
  top_feature: string
  feature_count: number
  computed_at: string
}

interface Feature {
  key: string
  label: string
  value: number
  expected: number
  z: number
  weight: number
  points: number
  detail: string
}

interface Baseline {
  id: string
  user_id: string
  peer_group: string
  window_start: string
  window_end: string
  days: number
  events: number
  daily_mean: number
  daily_stddev: number
  off_hours_share: number
  peer_size: number
  peer_daily_mean: number
  peer_daily_stddev: number
  peer_off_hours_share: number
  computed_at: string
}

interface ScoreDetail extends ScoreView {
  features: Feature[]
  top_features: Feature[]
  baseline?: Baseline
}

interface ComputeResponse {
  computed_at: string
  window_start: string
  window_end: string
  timezone: string
  days: number
  users: number
  events: number
  high_risk: number
  scores: ScoreView[]
}

const bandTone: Record<Band, string> = {
  low: 'sev-low',
  elevated: 'sev-medium',
  high: 'sev-high',
  critical: 'sev-critical',
}

const peerLabels: Record<string, string> = {
  owner: 'เจ้าของ',
  admin: 'ผู้ดูแลระบบ',
  audit_manager: 'ผู้จัดการตรวจสอบ',
  auditor: 'ผู้ตรวจสอบ',
  reviewer: 'ผู้สอบทาน',
  client_viewer: 'ผู้ชมฝั่งลูกค้า',
  member: 'สมาชิก',
  tenant: 'ทั้งองค์กร',
}

function peerLabel(value: string): string {
  return peerLabels[value] ?? value
}

function featureValue(f: Feature): string {
  if (f.key === 'daily_volume') return `${f.value.toFixed(1)} ครั้ง/วัน`
  return pct(f.value)
}

function featureExpected(f: Feature): string {
  if (f.key === 'daily_volume') return `${f.expected.toFixed(1)} ครั้ง/วัน`
  return pct(f.expected)
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

export function UEBA() {
  const qc = useQueryClient()
  const [days, setDays] = useState('30')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const scores = useQuery({
    queryKey: ['ueba-scores'],
    queryFn: () => list<ScoreView>('/v1/ueba/scores?limit=200'),
    staleTime: 30_000,
  })

  const rows = scores.data ?? []
  const activeId = selectedId ?? rows[0]?.id ?? null

  const detail = useQuery({
    queryKey: ['ueba-score', activeId],
    queryFn: () => api<ScoreDetail>(`/v1/ueba/scores/${activeId}`),
    enabled: Boolean(activeId),
    staleTime: 60_000,
  })

  const compute = useMutation({
    mutationFn: () => apiWrite<ComputeResponse>(`/v1/ueba/baselines?days=${days}`, 'POST'),
    onSuccess: (result) => {
      setNotice(
        `ประมวลผล ${number(result.users)} ผู้ใช้ จาก ${number(result.events)} เหตุการณ์ ` +
        `ในช่วง ${result.days} วัน (${result.timezone}) · พบกลุ่มเสี่ยงสูง ${number(result.high_risk)} ราย`,
      )
      setSelectedId(result.scores[0]?.id ?? null)
      void qc.invalidateQueries({ queryKey: ['ueba-scores'] })
      void qc.invalidateQueries({ queryKey: ['ueba-score'] })
    },
  })

  const maxRisk = rows.reduce((m, r) => Math.max(m, r.risk), 0)
  const highRisk = rows.filter((r) => r.risk >= 50).length
  const active = detail.data
  const baseline = active?.baseline

  return (
    <>
      <PageHead
        title="วิเคราะห์พฤติกรรมผู้ใช้"
        sub="ตรวจจับความเสี่ยงภายในจากพฤติกรรมเทียบกับกลุ่มเพื่อน · คะแนน 0–100 พร้อมคำอธิบายที่ตรวจสอบย้อนกลับได้"
        actions={
          <div className="row" style={{ gap: 8 }}>
            <label className="dim" htmlFor="ueba-days" style={{ fontSize: 12 }}>ช่วงเวลา</label>
            <select
              id="ueba-days"
              className="select"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              style={{ width: 120 }}
            >
              <option value="7">7 วัน</option>
              <option value="14">14 วัน</option>
              <option value="30">30 วัน</option>
              <option value="60">60 วัน</option>
              <option value="90">90 วัน</option>
            </select>
            <button
              className="btn btn-primary btn-sm"
              disabled={compute.isPending}
              onClick={() => { setNotice(null); compute.mutate() }}
            >
              {compute.isPending ? 'กำลังประมวลผล…' : 'ประมวลผลใหม่'}
            </button>
          </div>
        }
      />

      {notice ? (
        <div
          role="status"
          style={{
            marginTop: 4, marginBottom: 16, padding: '10px 14px', fontSize: 12.5,
            border: '1px solid var(--color-emerald-soft)', borderRadius: 6,
            color: 'var(--color-emerald-soft)',
          }}
        >
          {notice}
        </div>
      ) : null}

      {compute.isError ? (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {errorText(compute.error, 'ประมวลผลฐานพฤติกรรมไม่สำเร็จ')}
        </div>
      ) : null}

      <div className="grid-4 stagger">
        <Stat label="ผู้ใช้ที่ถูกประเมิน" value={number(rows.length)} unit="ราย" foot="มีกิจกรรมในช่วงเวลาที่เลือก" />
        <Stat label="คะแนนความเสี่ยงสูงสุด" value={maxRisk} unit="/ 100" foot="ยิ่งสูงยิ่งเบี่ยงเบนจากกลุ่มเพื่อน" />
        <Stat label="กลุ่มเสี่ยงสูง" value={number(highRisk)} unit="ราย" foot="คะแนน 50 ขึ้นไป ต้องตรวจสอบ" />
        <Stat
          label="กิจกรรมที่วิเคราะห์"
          value={compute.data ? number(compute.data.events) : '—'}
          unit={compute.data ? 'เหตุการณ์' : undefined}
          foot={compute.data ? `หน้าต่าง ${compute.data.days} วัน` : 'ยังไม่ได้ประมวลผลในเซสชันนี้'}
        />
      </div>

      {scores.isLoading ? (
        <div className="stack" style={{ marginTop: 16 }}><Skel h={200} /><Skel h={160} /></div>
      ) : scores.isError ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <div className="form-error">{errorText(scores.error, 'โหลดผลการวิเคราะห์ไม่สำเร็จ')}</div>
        </div>
      ) : rows.length === 0 ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <Empty
            title="ยังไม่มีผลการวิเคราะห์"
            body="กด “ประมวลผลใหม่” เพื่อสร้างฐานพฤติกรรมรายบุคคลจากเหตุการณ์ตรวจสอบ แล้วจัดอันดับความเสี่ยงเทียบกับกลุ่มเพื่อน"
          />
        </div>
      ) : (
        <div className="grid-main" style={{ marginTop: 16 }}>
          <div className="card card-pad">
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">อันดับความเสี่ยงรายผู้ใช้</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                เลือกแถวเพื่อดูคำอธิบายและค่าที่พบเทียบกับกลุ่มเพื่อน
              </span>
            </div>
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>ผู้ใช้</th>
                    <th>กลุ่ม</th>
                    <th style={{ textAlign: 'right' }}>ความเสี่ยง</th>
                    <th>ปัจจัยหลัก</th>
                    <th style={{ textAlign: 'right' }}>ประเมินเมื่อ</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedId(row.id)}
                      style={{
                        cursor: 'pointer',
                        background: row.id === activeId ? 'rgba(200, 169, 106, 0.06)' : undefined,
                      }}
                    >
                      <td>
                        <div>{row.display_name || 'ไม่ระบุชื่อ'}</div>
                        <div className="dim" style={{ fontSize: 11 }}>{row.user_id}</div>
                      </td>
                      <td className="dim">{peerLabel(row.peer_group)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <span className={`sev ${bandTone[row.band]}`}>
                          <i className="sev-dot" />
                          {row.risk}
                        </span>
                        <div className="dim" style={{ fontSize: 10.5 }}>{row.band_label}</div>
                      </td>
                      <td>{row.top_feature || 'ไม่พบปัจจัยที่เบี่ยงเบน'}</td>
                      <td className="dim tnum" style={{ textAlign: 'right', fontSize: 11 }}>
                        {thaiDateTime(row.computed_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="dim" style={{ fontSize: 11, marginTop: 10 }}>
              คะแนนคือผลรวมถ่วงน้ำหนักของ z-score 4 ปัจจัย · z ตั้งแต่ 6 ถือเป็นหลักฐานเต็มน้ำหนัก ·
              ช่วงนอกเวลาทำการคือก่อน 08:00 และตั้งแต่ 19:00 ตามเขตเวลาขององค์กร
            </div>
          </div>

          <div className="card card-pad">
            {detail.isLoading ? (
              <Skel h={240} />
            ) : detail.isError ? (
              <div className="form-error">{errorText(detail.error, 'โหลดคำอธิบายคะแนนไม่สำเร็จ')}</div>
            ) : !active ? (
              <Empty title="ยังไม่ได้เลือกผลคะแนน" body="เลือกผู้ใช้จากตารางเพื่อดูคำอธิบาย" />
            ) : (
              <>
                <div className="card-label">คำอธิบายคะแนน</div>
                <div className="row" style={{ alignItems: 'baseline', marginTop: 10 }}>
                  <span style={{ fontSize: 30, fontWeight: 300 }} className="tnum">{active.risk}</span>
                  <span className="dim" style={{ fontSize: 12 }}>/ 100</span>
                  <span className={`sev ${bandTone[active.band]}`} style={{ marginLeft: 8 }}>
                    <i className="sev-dot" />
                    {active.band_label}
                  </span>
                </div>
                <div style={{ fontSize: 12.5, marginTop: 8 }}>{active.explanation}</div>
                <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                  {active.display_name || 'ไม่ระบุชื่อ'} · เปรียบเทียบกับ {peerLabel(baseline?.peer_group ?? active.peer_group)}
                  {baseline && baseline.peer_size > 0 ? ` (${number(baseline.peer_size)} ราย)` : ''}
                </div>

                <div className="stack" style={{ marginTop: 16, gap: 10 }}>
                  {active.top_features.length === 0 ? (
                    <div className="dim" style={{ fontSize: 12 }}>
                      ไม่มีปัจจัยใดเบี่ยงเบนจากกลุ่มเปรียบเทียบในหน้าต่างนี้
                    </div>
                  ) : (
                    active.top_features.map((f) => (
                      <div key={f.key} style={{ borderTop: '1px solid var(--color-hairline)', paddingTop: 10 }}>
                        <div className="row" style={{ alignItems: 'baseline' }}>
                          <span style={{ fontSize: 12.5 }}>{f.label}</span>
                          <span className="tnum" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                            +{f.points.toFixed(1)} คะแนน
                          </span>
                        </div>
                        <div className="bar" style={{ marginTop: 6, width: '100%' }}>
                          <i
                            style={{
                              width: `${Math.min(100, (f.points / Math.max(f.weight, 0.01)) * 100)}%`,
                              background: f.points >= f.weight * 0.8
                                ? 'var(--color-crimson)'
                                : f.points >= f.weight * 0.4
                                  ? 'var(--color-amber)'
                                  : 'var(--color-gold)',
                            }}
                          />
                        </div>
                        <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>{f.detail}</div>
                        <div className="row tnum" style={{ gap: 14, marginTop: 6, fontSize: 10.5 }}>
                          <span>พบ {featureValue(f)}</span>
                          <span className="dim">คาดหวัง {featureExpected(f)}</span>
                          <span className="dim">z = {f.z.toFixed(2)}</span>
                          <span className="dim">น้ำหนัก {f.weight.toFixed(0)}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {baseline ? (
                  <div style={{ marginTop: 18 }}>
                    <div className="card-label">ฐานพฤติกรรมที่ใช้คำนวณ</div>
                    <div className="grid-2" style={{ marginTop: 10, gap: 10 }}>
                      <div>
                        <div className="dim" style={{ fontSize: 10.5 }}>กิจกรรมในช่วงเวลา</div>
                        <div className="tnum" style={{ fontSize: 13 }}>{number(baseline.events)} ครั้ง · {baseline.days} วัน</div>
                      </div>
                      <div>
                        <div className="dim" style={{ fontSize: 10.5 }}>เฉลี่ยต่อวัน (SD)</div>
                        <div className="tnum" style={{ fontSize: 13 }}>
                          {baseline.daily_mean.toFixed(1)} ({baseline.daily_stddev.toFixed(1)})
                        </div>
                      </div>
                      <div>
                        <div className="dim" style={{ fontSize: 10.5 }}>สัดส่วนนอกเวลา</div>
                        <div className="tnum" style={{ fontSize: 13 }}>
                          {pct(baseline.off_hours_share)}
                          <span className="dim"> · กลุ่มเพื่อน {pct(baseline.peer_off_hours_share)}</span>
                        </div>
                      </div>
                      <div>
                        <div className="dim" style={{ fontSize: 10.5 }}>เฉลี่ยกลุ่มเพื่อน (SD)</div>
                        <div className="tnum" style={{ fontSize: 13 }}>
                          {baseline.peer_daily_mean.toFixed(1)} ({baseline.peer_daily_stddev.toFixed(1)})
                        </div>
                      </div>
                    </div>
                    <div className="dim" style={{ fontSize: 10.5, marginTop: 10 }}>
                      หน้าต่าง {thaiDateTime(baseline.window_start)} ถึง {thaiDateTime(baseline.window_end)}
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}
